/**
 * Text Reveal: body copy that sits dimmed and fills in word by word as it
 * crosses the reading line (Figma 80th-letter frame — the whole letter at 10%
 * opacity with the lines already read at full strength).
 *
 * This module only splits the text into per-word spans; the reveal itself is a
 * scroll-driven CSS animation declared in snippets/text.liquid, one anonymous
 * `view()` timeline per word. Nothing runs on the main thread while scrolling,
 * and the reveal is scroll-linked rather than a one-shot transition, so
 * scrolling back up dims the copy again.
 *
 * The dimmed state is keyed to `html.motion-enabled`, the flag the inline
 * script in snippets/scripts.liquid sets before first paint (and drops again if
 * the motion system never boots). That is what keeps the copy from flashing:
 * the letter is already dim in the first frame rather than starting solid and
 * dimming once this module lands. It is also why the split bails out without
 * the flag — in the theme editor, or with JS off, the stylesheet leaves the
 * copy plain and there is nothing to hand over to.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const SPLIT_CLASS = 'text-reveal--split';
const WORD_CLASS = 'text-reveal__word';

/* One `view()` timeline per word is cheap but not free, and a rich text field
   has no length limit. Past the cap the tail is left as plain text: it reveals
   with the paragraph around it rather than word by word, which on copy that
   long is already off-screen of any single reading pass. */
const WORD_LIMIT = 1200;

class TextReveal extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#split();
  }

  /**
   * Wraps every word of the element's text in a span, leaving the whitespace
   * runs as text nodes of their own so line breaking, justification and
   * copy-paste come out exactly as they did before the split. Inline markup
   * (links, `<strong>`, `<em>`) is walked into rather than over, so a bold
   * word gets its span inside the `<strong>` and keeps its formatting.
   */
  #split() {
    if (this.classList.contains(SPLIT_CLASS)) return;
    // Matches the stylesheet's own three guards, so the split never happens
    // against copy the CSS has decided to leave plain. motion.js bails on the
    // same flag, which is how the theme keeps reveal animations out of the
    // editor while merchants arrange sections.
    if (!document.documentElement.classList.contains('motion-enabled')) return;
    if (prefersReducedMotion()) return;
    if (!CSS.supports('animation-timeline: view()')) return;

    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Whitespace-only nodes carry no word, and anything already inside a
        // word span means this element has been split before (a block-level
        // re-render in the theme editor re-runs connectedCallback).
        if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
        if (node.parentElement?.closest(`.${WORD_CLASS}`)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    /** @type {Text[]} */
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      if (node instanceof Text) textNodes.push(node);
    }

    let words = 0;

    for (const textNode of textNodes) {
      const fragment = document.createDocumentFragment();

      // The capturing group keeps the separators in the result, so the
      // whitespace is re-emitted verbatim instead of collapsed to a space.
      for (const part of (textNode.nodeValue ?? '').split(/(\s+)/)) {
        if (part === '') continue;

        if (part.trim() === '' || words >= WORD_LIMIT) {
          fragment.append(part);
          continue;
        }

        const word = document.createElement('span');
        word.className = WORD_CLASS;
        word.textContent = part;
        fragment.append(word);
        words += 1;
      }

      textNode.parentNode?.replaceChild(fragment, textNode);
    }

    if (words > 0) this.classList.add(SPLIT_CLASS);
  }
}

if (!customElements.get('text-reveal')) {
  customElements.define('text-reveal', TextReveal);
}
